import * as THREE from 'three';
import { ParticleSystem } from './Particles.js';
import { Decals } from './Decals.js';
import { Tracers } from './Tracers.js';
import { Muzzle, FlashLights } from './Muzzle.js';
import { Impacts } from './Impacts.js';

/**
 * FXSystem — the façade for everything that happens in the moment of violence.
 *
 * It owns the pools, subscribes to the event vocabulary, and registers exactly one
 * update on the `'fx'` phase. No other module talks to the particle engine, the
 * decal pool or the tracer buffer directly; they emit events and this listens.
 *
 * Two pieces of glue live here rather than in a subsystem because they need
 * information from more than one event:
 *
 *  - **Tracers** need both ends of the shot. `weapon:fire` gives the origin and the
 *    direction; `hit:surface` gives the impact point, and arrives later in the same
 *    frame. The pending shot is held until the hit resolves (or until the next
 *    frame, if the round hit nothing).
 *  - **Impact direction.** `hit:surface` carries no direction, but sparks fired
 *    back along the surface normal look wrong — spall follows the reflection of
 *    the bullet. The last known shot direction is remembered and used.
 */

export const TUNING = {
  pendingShotTimeout: 0.08, // s to wait for a hit before assuming the round flew on
  strayTracerRange: 120,
  sunSyncInterval: 0.25,
  muzzleFreshness: 0.15, // s that a muzzle position stays valid as a tracer origin
};

export class FXSystem {
  constructor(ctx) {
    this.ctx = ctx;

    this.root = new THREE.Group();
    this.root.name = 'fx';
    this.root.matrixAutoUpdate = false;
    ctx.scene.add(this.root);

    this.particles = new ParticleSystem(ctx, this.root);
    this.decals = new Decals(ctx, this.root);
    this.tracers = new Tracers(ctx, this.root, this.particles);
    this.lights = new FlashLights(ctx);
    this.muzzle = new Muzzle(ctx, this.root, this.particles, this.lights);
    this.impacts = new Impacts(ctx, this.particles, this.decals, this.lights);

    /* ---- pending-shot bookkeeping (pre-allocated) ---- */
    this._shot = { active: false, t: 0, ox: 0, oy: 0, oz: 0, dx: 0, dy: 0, dz: 0, tracer: false };
    this._lastDir = new THREE.Vector3(0, 0, -1);
    this._sunDir = new THREE.Vector3(0.5, 0.8, 0.35).normalize();
    this._sunColor = new THREE.Color(1.5, 1.35, 1.12);
    this._ambient = new THREE.Color(0.32, 0.37, 0.46);
    this._sunTimer = 999;
    this._tmp = new THREE.Vector3();
    this._mat = new THREE.Matrix4();

    /* ---- profiling ---- */
    this.ms = 0;
    this.msPeak = 0;
    this._msAcc = 0;
    this._msN = 0;

    this._offs = [];
    this._wire();

    ctx.engine.registerUpdate((dt, elapsed) => this.update(dt, elapsed), 'fx');
    this._installDebug();

    // Warm the depth-prepass programs now rather than on the first shot fired:
    // compiling an override-material variant for every geometry in the level is a
    // visible hitch if it happens mid-firefight.
    this._syncSun();
    try {
      this.particles.depth.render(99);
    } catch (err) {
      console.warn('[fx] depth prepass warmup skipped', err);
    }
  }

  /* ------------------------------ event wiring ----------------------------- */

  _wire() {
    const on = (name, fn) => this._offs.push(this.ctx.events.on(name, fn));

    on('fx:muzzle', (e) => {
      if (!e) return;
      this.muzzle.flash(e.matrix, e.scale ?? 1, this._elapsed ?? 0);
    });

    on('fx:shell', (e) => {
      if (!e?.position) return;
      const p = e.position;
      const v = e.velocity ?? _zero;
      this.impacts.shell(p.x, p.y, p.z, v.x ?? 0, v.y ?? 0, v.z ?? 0);
    });

    on('weapon:fire', (e) => {
      if (!e?.dir) return;
      this._lastDir.set(e.dir.x, e.dir.y, e.dir.z).normalize();
      const s = this._shot;
      s.active = true;
      s.t = 0;
      s.dx = this._lastDir.x; s.dy = this._lastDir.y; s.dz = this._lastDir.z;
      // Prefer the real muzzle position over the aim origin (usually the eye), so
      // the tracer leaves the barrel rather than the player's face.
      const fresh = this._elapsed - this.muzzle.lastTime < TUNING.muzzleFreshness;
      const src = fresh ? this.muzzle.lastWorldPos : e.origin;
      s.ox = src?.x ?? 0; s.oy = src?.y ?? 0; s.oz = src?.z ?? 0;
      s.tracer = this.tracers.shouldTrace();
    });

    on('hit:surface', (e) => {
      if (!e?.point) return;
      const p = e.point;
      const n = e.normal ?? _up;
      const d = this._shotDirTo(p.x, p.y, p.z);
      this.impacts.surface(p.x, p.y, p.z, n.x, n.y, n.z, e.material ?? 'concrete', d.x, d.y, d.z);
      this._resolveTracer(p.x, p.y, p.z);
    });

    on('hit:actor', (e) => {
      if (!e?.point) return;
      const p = e.point;
      let dx, dy, dz;
      if (e.dir) {
        const l = Math.hypot(e.dir.x, e.dir.y, e.dir.z) || 1;
        dx = e.dir.x / l; dy = e.dir.y / l; dz = e.dir.z / l;
      } else {
        const d = this._shotDirTo(p.x, p.y, p.z);
        dx = d.x; dy = d.y; dz = d.z;
      }
      this.impacts.actor(p.x, p.y, p.z, dx, dy, dz, e.headshot);
      this._resolveTracer(p.x, p.y, p.z);
    });

    on('actor:death', (e) => {
      const a = e?.actor;
      const p = a?.position;
      if (!p) return;
      this.impacts.death(p.x, p.y, p.z);
    });

    on('player:footstep', (e) => {
      const cam = this.ctx.camera;
      const feet = (this.ctx.player?.position?.y ?? cam.position.y - 1.65);
      this.impacts.footstep(cam.position.x, feet + 0.02, cam.position.z, e?.material ?? 'concrete');
    });

    on('fx:explosion', (e) => {
      if (!e?.position) return;
      const p = e.position;
      this.impacts.explosion(p.x, p.y, p.z, e.power ?? 1);
    });
  }

  /** Direction of the shot that produced a hit; falls back to camera-to-point. */
  _shotDirTo(x, y, z) {
    const s = this._shot;
    if (s.active) {
      this._tmp.set(s.dx, s.dy, s.dz);
      return this._tmp;
    }
    const c = this.ctx.camera.position;
    this._tmp.set(x - c.x, y - c.y, z - c.z);
    const l = this._tmp.length() || 1;
    return this._tmp.divideScalar(l);
  }

  _resolveTracer(x, y, z) {
    const s = this._shot;
    if (!s.active) return;
    if (s.tracer) this.tracers.fire(s.ox, s.oy, s.oz, x, y, z);
    s.active = false;
  }

  /* --------------------------------- update -------------------------------- */

  update(dt, elapsed) {
    const t0 = performance.now();
    this._elapsed = elapsed;

    // Bring the camera's world matrix up to date before anything reads it; the
    // renderer would not do it until after this phase.
    const cam = this.ctx.camera;
    cam.updateMatrixWorld();
    cam.matrixWorldInverse.copy(cam.matrixWorld).invert();

    // A round that hit nothing still gets a tracer, sent off into the distance.
    const s = this._shot;
    if (s.active) {
      s.t += dt;
      if (s.t > TUNING.pendingShotTimeout) {
        if (s.tracer) {
          const r = TUNING.strayTracerRange;
          this.tracers.fire(s.ox, s.oy, s.oz, s.ox + s.dx * r, s.oy + s.dy * r, s.oz + s.dz * r);
        }
        s.active = false;
      }
    }

    this._sunTimer += dt;
    if (this._sunTimer > TUNING.sunSyncInterval) {
      this._sunTimer = 0;
      this._syncSun();
    }

    this.lights.update(dt);
    this.muzzle.update(dt);
    this.particles.update(dt, elapsed);
    this.decals.update(dt, elapsed);
    this.tracers.update(dt, elapsed);

    const ms = performance.now() - t0;
    this._msAcc += ms;
    this._msN++;
    if (this._msN >= 30) {
      this.ms = this._msAcc / this._msN;
      this.msPeak = Math.max(this.msPeak * 0.9, ms);
      this._msAcc = 0;
      this._msN = 0;
    }
  }

  /**
   * Pull the sun out of the lighting module if it has published one, so smoke and
   * decals are lit by the same key light as the world. Guarded: the lighting agent
   * may not have landed yet.
   */
  _syncSun() {
    const lighting = this.ctx.lighting;
    const sun = lighting?.sun;
    if (lighting?.sunDirection) {
      // Lighting publishes a unit vector pointing *at* the sun, which is exactly
      // the convention the particle and decal shaders want.
      this._sunDir.copy(lighting.sunDirection).normalize();
    } else if (sun?.isDirectionalLight) {
      this._sunDir.copy(sun.position);
      if (sun.target) this._sunDir.sub(sun.target.position);
      this._sunDir.normalize();
    }
    if (sun?.color) {
      const i = Math.min(sun.intensity ?? 1, 6);
      this._sunColor.copy(sun.color).multiplyScalar(0.30 + i * 0.22);
    }
    const hemi = lighting?.hemi ?? lighting?.ambient;
    if (hemi?.color) this._ambient.copy(hemi.color).multiplyScalar(0.55 * (hemi.intensity ?? 1));
    this.particles.setSun(this._sunDir, this._sunColor, this._ambient);
    this.decals.setSun(this._sunDir, this._sunColor, this._ambient);
  }

  /* ---------------------------- debug harness ------------------------------ */

  /**
   * `window.__fxtest` drives every effect without the weapon system existing, which
   * is the only way to iterate on impacts while ballistics is still being written.
   */
  _installDebug() {
    const ctx = this.ctx;
    const fwd = new THREE.Vector3();
    const api = {
      /** Shoot the crosshair: raycast the world and play the real impact. */
      impact: (material = 'concrete', dist = 60) => {
        const cam = ctx.camera;
        cam.getWorldDirection(fwd);
        const hit = ctx.physics?.raycast(cam.position, fwd, dist, 0xffff);
        if (hit?.hit) {
          ctx.events.emit('weapon:fire', { weapon: 'debug', origin: cam.position, dir: fwd, spread: 0 });
          ctx.events.emit('hit:surface', { point: hit.point, normal: hit.normal, material, entity: null });
        } else {
          const p = cam.position.clone().addScaledVector(fwd, 8);
          ctx.events.emit('weapon:fire', { weapon: 'debug', origin: cam.position, dir: fwd, spread: 0 });
          ctx.events.emit('hit:surface', { point: p, normal: fwd.clone().negate(), material, entity: null });
        }
        return material;
      },
      /** Impacts at a fixed spot ahead of the camera, for side-by-side comparison. */
      at: (material, offX = 0, offY = 0, dist = 6) => {
        const cam = ctx.camera;
        cam.getWorldDirection(fwd);
        const p = cam.position.clone().addScaledVector(fwd, dist);
        p.x += offX;
        p.y += offY;
        ctx.events.emit('hit:surface', { point: p, normal: fwd.clone().negate(), material, entity: null });
      },
      burst: (material = 'concrete', n = 6, delay = 90) => {
        let i = 0;
        const t = setInterval(() => {
          api.impact(material);
          if (++i >= n) clearInterval(t);
        }, delay);
      },
      muzzle: (scale = 1) => {
        const cam = ctx.camera;
        cam.getWorldDirection(fwd);
        const m = new THREE.Matrix4();
        const pos = cam.position.clone().addScaledVector(fwd, 0.6).add(new THREE.Vector3(0, -0.12, 0));
        m.lookAt(pos, pos.clone().add(fwd), new THREE.Vector3(0, 1, 0));
        m.setPosition(pos);
        ctx.events.emit('fx:muzzle', { matrix: m, scale });
      },
      shell: () => {
        const cam = ctx.camera;
        cam.getWorldDirection(fwd);
        const right = new THREE.Vector3().crossVectors(fwd, new THREE.Vector3(0, 1, 0)).normalize();
        const p = cam.position.clone().addScaledVector(fwd, 0.5).addScaledVector(right, 0.15);
        ctx.events.emit('fx:shell', { position: p, velocity: right.clone().multiplyScalar(2.5).setY(1.8) });
      },
      explosion: (power = 1, dist = 12) => {
        const cam = ctx.camera;
        cam.getWorldDirection(fwd);
        const p = cam.position.clone().addScaledVector(fwd, dist);
        p.y = (ctx.physics?.groundHeight(p.x, p.z, p.y + 4, 20) ?? 0) + 0.3;
        ctx.events.emit('fx:explosion', { position: p, power });
      },
      death: () => {
        const cam = ctx.camera;
        cam.getWorldDirection(fwd);
        const p = cam.position.clone().addScaledVector(fwd, 4);
        ctx.events.emit('hit:actor', { actor: null, point: p, normal: fwd.clone().negate(), damage: 100, headshot: true, dir: fwd.clone() });
        ctx.events.emit('actor:death', { actor: { position: p }, headshot: true });
      },
      /** One of every material, laid out left to right on the wall in front. */
      all: (dist = 6) => {
        const mats = ['concrete', 'metal', 'wood', 'dirt', 'sand', 'glass', 'flesh', 'water'];
        mats.forEach((m, i) => api.at(m, (i - 3.5) * 0.9, 0.4, dist));
        return mats;
      },
      stats: () => ({
        ms: +this.ms.toFixed(3),
        peakMs: +this.msPeak.toFixed(3),
        softParticles: this.particles.soft.geometry.instanceCount,
        addParticles: this.particles.add.geometry.instanceCount,
        capacity: this.particles.soft.capacity + this.particles.add.capacity,
        chips: this.particles.chips.count,
        shards: this.particles.shards.count,
        embers: this.particles.embers.count,
        shells: this.particles.shells.count,
        decals: this.decals.geometry.instanceCount,
        decalCapacity: this.decals.capacity,
        softDepthPass: this.particles.depth.enabled && !this.particles.depth.failed,
        drawCalls: ctx.renderer.info.render.calls,
      }),
      fx: this,
    };
    window.__fxtest = api;
    this._installHashScenarios(api);
  }

  /**
   * Reload-proof scenario driver, driven from the URL hash.
   *
   * Eight agents share one dev server, so the page hot-reloads several times a
   * minute — reliably often enough to lose any state set up from the console
   * before a screenshot of it can be taken. Encoding the shot in the URL means the
   * page rebuilds it for itself after every reload, which is the only way to judge
   * an effect against a specific piece of the level. `src/fx/shaders/_preview.html`
   * does the same thing for the same reason.
   *
   * Inert unless the hash contains `fx=`, so normal play never sees it.
   *
   *   #fx=explosion&at=0,0.8,33&pose=0,0.02,47,0,-0.42&every=900
   *   #fx=burst&mat=metal&pose=0,0.02,47,-0.95,0&every=700
   *   #fx=muzzle&every=200
   */
  _installHashScenarios(api) {
    const raw = location.hash.replace(/^#/, '');
    if (!raw.includes('fx=')) return;
    const q = new URLSearchParams(raw);
    const num = (v, d) => {
      const n = parseFloat(v);
      return Number.isFinite(n) ? n : d;
    };
    const list = (name) => (q.get(name) || '').split(',').map(Number);
    const pose = list('pose');
    const at = list('at');
    const what = q.get('fx');
    const mat = q.get('mat') || 'concrete';
    const every = num(q.get('every'), 1200);

    const run = () => {
      const pl = this.ctx.player;
      if (pl && pose.length === 5 && pose.every(Number.isFinite)) {
        pl.position.set(pose[0], pose[1], pose[2]);
        pl.yaw = pose[3];
        pl.pitch = pose[4];
      }
      document.querySelector('.hud-menu')?.classList.remove('is-open');
      switch (what) {
        case 'explosion':
          if (at.length === 3 && at.every(Number.isFinite)) {
            this.impacts.explosion(at[0], at[1], at[2], num(q.get('power'), 1));
          } else {
            api.explosion(num(q.get('power'), 1), num(q.get('dist'), 12));
          }
          break;
        case 'impact': api.impact(mat, num(q.get('dist'), 60)); break;
        case 'burst': api.burst(mat, num(q.get('n'), 6), num(q.get('gap'), 80)); break;
        case 'muzzle': api.muzzle(num(q.get('scale'), 1)); break;
        case 'all': api.all(num(q.get('dist'), 6)); break;
        case 'death': api.death(); break;
        case 'light': {
          const fin = (v, d) => (Number.isFinite(v) ? v : d);
          this.lights.pulse(fin(at[0], 0), fin(at[1], 1), fin(at[2], 0), 0xffb060,
            num(q.get('i'), 130), num(q.get('r'), 15), 0.35);
          break;
        }
        default: break;
      }
    };

    // The level is still being built when the FX system is constructed, so the
    // first run has to wait for the player to exist.
    setTimeout(run, 400);
    if (every > 0) this._hashTimer = setInterval(run, every);
  }

  dispose() {
    if (this._hashTimer) clearInterval(this._hashTimer);
    for (const off of this._offs) off();
    this._offs.length = 0;
    this.particles.dispose();
    this.decals.dispose();
    this.tracers.dispose();
    this.muzzle.dispose();
    this.ctx.scene.remove(this.root);
    if (window.__fxtest?.fx === this) delete window.__fxtest;
  }
}

const _zero = { x: 0, y: 0, z: 0 };
const _up = { x: 0, y: 1, z: 0 };
